import { memo, type ReactNode } from "react";
import { Button, type ButtonProps, Dialog, DialogActions, DialogContent, type DialogProps, Stack } from "~/common/mui";
import { useIsMinTabletSize } from "../hooks/useMediaQuery";
import { deepmerge } from "../mui/utils";

interface Props {
  label?: ReactNode;
  okLabel: ReactNode;
  cancelLabel: ReactNode;
  onOk?: () => void;
  onCancel: () => void;
  children?: ReactNode;
  hideClose?: boolean;
  slotProps?: {
    okButton?: ButtonProps;
    cancelButton?: ButtonProps;
  };
}

/**
 * Use either label or children - label takes precedence over children
 */
const ConfirmationModal = ({ label, okLabel, cancelLabel, onCancel, onOk, children, slotProps }: Props) => {
  const isTablet = useIsMinTabletSize();

  const handleClose: DialogProps["onClose"] = (e: React.MouseEvent<HTMLAnchorElement>, reason) => {
    e.preventDefault();
    e.stopPropagation();
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return; // Prevent closing on outside click or escape key
    }
    onCancel();
  };

  const okButtonProps: ButtonProps = deepmerge(
    {
      children: okLabel,
      onClick: onOk,
    },
    slotProps?.okButton,
  );
  const cancelButtonProps: ButtonProps = deepmerge(
    {
      children: cancelLabel,
      onClick: onCancel,
    },
    slotProps?.cancelButton,
  );

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: (theme) => ({
            width: "312px !important",
            [theme.breakpoints.up("desktop-s")]: {
              width: "720px !important",
            },
          }),
        },
      }}
    >
      <DialogContent
        sx={{
          overflow: "hidden",
        }}
      >
        {label ? <p className={"headline-small"}>{label}</p> : children}
      </DialogContent>
      <DialogActions>
        <Stack gap={1} direction={"row"}>
          <Button
            fullWidth={!isTablet}
            variant={"text"}
            {...cancelButtonProps}
            data-test-id={"confirmation-modal-cancel"}
          >
            {cancelLabel}
          </Button>
          <Button fullWidth={!isTablet} {...okButtonProps} data-test-id={"confirmation-modal-ok"} />
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default memo(ConfirmationModal);
